/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { InlineNotification } from 'carbon-components-react';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { getBranding } from '../../../../ia/util/branding';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { useSpeechToText } from '../../../hooks/useSpeechCommon';
import {
  shouldHideInstancePicker,
  selectSTTInstanceCreateRequired
} from '../../../store/phone/selectors';
import { createSpeechToTextInstance } from '../../../store/voice/actions';
import {
  selectCurrentSpeechToTextInstance,
  selectDefaultSpeechToTextModel,
  selectSpeechToTextCreateStatus
} from '../../../store/voice/selectors';
import { Speech } from '../messages';
import { FIELDS } from '../schema';
import { CreateSpeechInstanceButton } from '../speech-common/CreateSpeechInstanceButton';
import { SpeechInstanceTable } from '../speech-common/SpeechInstanceTable';
import { CustomModels } from './CustomModels';
import { SpeechToText as messages } from './messages';
import { Models } from './Models';

interface SpeechToTextProps {
  size: string;
  readOnly: boolean;
}
export const SpeechToText = ({ size, readOnly }: SpeechToTextProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  // Use the speechCommon hook to get the instances & such
  const {
    createError,
    showPremiumWarning,
    instances,
    handleSelect,
    loadingInstances
  } = useSpeechToText();

  const formMethods = useFormContext();
  const { updateFieldValue } = useFormHelpers(formMethods);

  const [hasCreated, setHasCreated] = useState<boolean>(false);

  const currentSTTInstance = useAppSelector(selectCurrentSpeechToTextInstance);
  const STTInstanceCreateRequired = useAppSelector(
    selectSTTInstanceCreateRequired
  );
  const STTDefaultModel = useAppSelector(selectDefaultSpeechToTextModel);
  const { productName } = getBranding();

  useEffect(() => {
    if (hasCreated && currentSTTInstance && STTDefaultModel) {
      updateFieldValue(FIELDS.STT.INSTANCE_ID, currentSTTInstance?.id);
      updateFieldValue(FIELDS.STT.URL, currentSTTInstance?.credentials?.url);
      updateFieldValue(
        FIELDS.STT.APIKEY,
        currentSTTInstance?.credentials?.apikey
      );
      updateFieldValue(FIELDS.STT.NARROWBAND.MODEL, STTDefaultModel);
    }
  }, [hasCreated, currentSTTInstance, STTDefaultModel, updateFieldValue]);

  // Prevent the instance selection picker from being shown in AWS specifically;
  // it should, however, be shown on IBM Cloud (whether standalone or under WXO)
  if (shouldHideInstancePicker()) {
    return (
      <div className="TabContent__body-content">
        <Models readOnly={readOnly} />
        {size === 'lg' && <CustomModels />}
      </div>
    );
  }
  return (
    <>
      {STTInstanceCreateRequired ? (
        <div className="TabContent__body-content">
          <p>
            <FormattedMessage
              {...messages.createMessage}
              values={{ productName }}
            />
          </p>
          {createError && (
            <InlineNotification
              kind="warning"
              title={_.get(createError, 'error') ?? 'Failure'}
              subtitle={_.get(createError, 'trace') ?? createError}
              hideCloseButton={true}
            />
          )}
          <div className="TabContent__spacer24">
            <CreateSpeechInstanceButton
              statusSelector={selectSpeechToTextCreateStatus}
              create={async () => {
                await dispatch(createSpeechToTextInstance());
                setHasCreated(true);
              }}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="TabContent__body-content">
            <h4>
              <FormattedMessage {...messages.instanceSubtitle} />
            </h4>
            <p className="TabContent__subtitle">
              <FormattedMessage {...messages.instanceDescription} />
            </p>
          </div>
          {!loadingInstances && showPremiumWarning && (
            <InlineNotification
              kind="warning"
              title=""
              className="Phone__Speech-PremiumSpeechWarning"
              subtitle={t(Speech.entDataIsolWarning)}
              iconDescription="close warning"
              statusIconDescription="close warning"
            />
          )}
          <div className="TabContent__spacer16" />
          {currentSTTInstance?.id && (
            <>
              <SpeechInstanceTable
                readOnly={readOnly}
                instances={instances}
                defaultSelection={currentSTTInstance?.id}
                handleSelect={handleSelect}
                loading={loadingInstances}
                size="sm"
              />
              <div className="TabContent__spacer8" />
              <div className="TabContent__body-content">
                <Models readOnly={readOnly} />
                {size === 'lg' && <CustomModels />}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};
